import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";

// Context-fed action hook for the represented products/companies collections — the sibling of
// use_pr_experts_api (which owns experts). The provider owns the deduped collections; this only
// performs the writes: create (add) with silentReload picking up the new row, and remove with an
// optimistic drop on the provider state + snapshot rollback + throw-on-error, routing failures to the
// shared generalError and reconciling via silentReload.
//
// Remove fans out: the representations view carries one row per promoter job, so a deduped collection
// row keeps ALL representation ids for that entity in `representationIds`; removing an entity destroys
// every one of them. Products/companies have no self-representation, so there's no self_expert case.
const usePrRepresentationsApi = ({
  prProfileId,
  representedProducts,
  setRepresentedProducts,
  representedCompanies,
  setRepresentedCompanies,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  // Re-sync page state from whatever the server actually committed after a failed write — the local
  // rollback restores the pre-write collection, which is only right if the write truly did nothing (a
  // partially-applied fan-out destroy is exactly the case where it isn't). Nonfatal so a refresh that
  // also fails cannot escalate a recoverable, already-surfaced error into the page error boundary.
  const reconcile = async () => {
    try {
      await silentReload({ nonfatal: true });
    } catch {
      // Deliberately empty: the failure is already on generalError.
    }
  };

  // Resolves { saved: boolean } — the contract every PR add method shares (addExpert included, via its
  // own retrofit) so a caller can gate a post-add side effect (like requesting a tab switch) on a
  // CONFIRMED save rather than firing it unconditionally. No local collection to update optimistically
  // on a create — the row doesn't exist until silentReload re-fetches it, same as addExpert.
  const addProduct = async (productId) => {
    if (isUpdating) return { saved: false }; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      throwOnApiError(await qapi.representations.create({ promoterProfileId: prProfileId, promoEntityId: productId }));
      await silentReload();
      return { saved: true };
    } catch (e) {
      setGeneralError(e);
      return { saved: false };
    } finally {
      setIsUpdating(false);
    }
  };

  const addCompany = async (companyId) => {
    if (isUpdating) return { saved: false }; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      throwOnApiError(await qapi.representations.create({ promoterProfileId: prProfileId, promoEntityId: companyId }));
      await silentReload();
      return { saved: true };
    } catch (e) {
      setGeneralError(e);
      return { saved: false };
    } finally {
      setIsUpdating(false);
    }
  };

  // Resolves whether the entity was removed, which is what closes the section's confirmation.
  const makeRemover = (collection, setCollection) => async (row) => {
    if (isUpdating) return false; // guard overlapping mutations — a whole-array snapshot can't survive concurrency
    setGeneralError(null);
    const snapshot = collection;
    const entityId = row.id;

    // Optimistically drop the entity from the provider collection (functional update — no stale closure).
    setCollection((current) => current.filter((entry) => entry.id !== entityId));

    const representationIds = snapshot
      .filter((entry) => entry.id === entityId)
      .flatMap((entry) => entry.representationIds || []);

    setIsUpdating(true);
    try {
      // allSettled, not all: an early rejection must not start the reconcile (or release the isUpdating
      // guard) while sibling destroys are still in flight — every one has to land before the collection
      // is re-read. The first failure still surfaces.
      const outcomes = await Promise.allSettled(
        representationIds.map(async (id) => throwOnApiError(await qapi.representations.destroy(id))),
      );
      const failed = outcomes.find((outcome) => outcome.status === "rejected");
      if (failed) throw failed.reason;
      await silentReload();
      return true;
    } catch (e) {
      setCollection(snapshot); // roll back the optimistic removal
      setGeneralError(e);
      await reconcile();
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    addProduct,
    addCompany,
    removeProduct: makeRemover(representedProducts, setRepresentedProducts),
    removeCompany: makeRemover(representedCompanies, setRepresentedCompanies),
  };
};

export default usePrRepresentationsApi;
